import axios from 'axios';
import get from 'lodash/get';
import { getUrl } from '@/config/api.routes';

const basePath = 'aimanager.mcpConnectors';

// A connector is agent-only until someone widens it: customer chat is the
// surface where an end customer indirectly triggers a third-party call.
export const SENDER_TYPES = ['agent', 'customer', 'both'];
export const DEFAULT_SENDER_TYPE = 'agent';

// Methods the backend treats as read-only. Anything else needs allow_write
// before it is handed to a model, so the page can flag the difference.
export const READ_ONLY_METHODS = ['get', 'head'];

/*
 * Everything a PUT may change. endpoint_id is absent on purpose: a different
 * endpoint is a different tool, and the backend rejects a change to it.
 * Shared so the toggle and the editor cannot drift on what a connector is.
 */
export const EDITABLE_FIELDS = ['tool_name', 'purpose', 'sender_type', 'enabled', 'allow_write'];

export default {
  namespaced: true,
  state: {
    connectors: [],
    connectorItem: null,
  },
  getters: {
    getConnectors(state) {
      return state.connectors;
    },
    getConnectorItem(state) {
      return state.connectorItem;
    },
    /*
     * The page renders endpoints, not connectors, so it needs to look up an
     * endpoint's connector by endpoint id. One connector per endpoint is
     * enforced by a unique index on the backend, so a plain map is enough.
     */
    connectorsByEndpointId(state) {
      return state.connectors.reduce((acc, connector) => {
        acc[connector.endpoint_id] = connector;
        return acc;
      }, {});
    },
  },
  mutations: {
    SET_CONNECTORS(state, connectors) {
      state.connectors = connectors;
    },
    SET_CONNECTOR_ITEM(state, connector) {
      state.connectorItem = connector;
    },
    // Create and update land in the same place: both respond with the whole
    // connector, and the page only ever needs the current one per endpoint.
    UPSERT_CONNECTOR(state, connector) {
      const index = state.connectors.findIndex(({ id }) => id === connector.id);
      if (index === -1) {
        state.connectors = [...state.connectors, connector];
      } else {
        state.connectors = state.connectors.map(
          existing => (existing.id === connector.id ? connector : existing),
        );
      }
      if (get(state.connectorItem, 'id') === connector.id) {
        state.connectorItem = connector;
      }
    },
    REMOVE_CONNECTOR(state, id) {
      state.connectors = state.connectors.filter(connector => connector.id !== id);
      if (get(state.connectorItem, 'id') === id) {
        state.connectorItem = null;
      }
    },
  },
  actions: {
    // GET /mcp_connectors
    // Every connector, enabled or not, with the endpoint and domain it points at.
    async listConnectors({ commit }) {
      const response = await axios.get(getUrl(basePath));
      commit('SET_CONNECTORS', get(response, 'data.data', []));
      return response;
    },

    // GET /mcp_connectors/:id
    // One connector, for the editor. Re-read rather than taken from the list so
    // the editor opens on what is stored rather than on a stale row.
    async getConnector({ commit }, id) {
      const response = await axios.get(`${getUrl(basePath)}/${id}`);
      commit('SET_CONNECTOR_ITEM', get(response, 'data', null));
      return response;
    },

    // POST /mcp_connectors
    // `purpose` is required: it is what tells a model when to use the tool.
    async createConnector({ commit }, payload) {
      const response = await axios.post(getUrl(basePath), payload);
      commit('UPSERT_CONNECTOR', get(response, 'data'));
      return response;
    },

    // PUT /mcp_connectors/:id
    // endpoint_id is not editable, so only the connector's own fields go here.
    async updateConnector({ commit }, { id, body }) {
      const response = await axios.put(`${getUrl(basePath)}/${id}`, body);
      commit('UPSERT_CONNECTOR', get(response, 'data'));
      return response;
    },

    // DELETE /mcp_connectors/:id
    async deleteConnector({ commit }, id) {
      const response = await axios.delete(`${getUrl(basePath)}/${id}`);
      commit('REMOVE_CONNECTOR', id);
      return response;
    },

    clearConnectorItem({ commit }) {
      commit('SET_CONNECTOR_ITEM', null);
    },
  },
};
